import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState, VisibilityState } from '@tanstack/react-table'
import type { ClientResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import {
  BulkActionBar,
  ColumnVisibilityMenu,
  DataTable,
  RowActions,
} from '@trellis/roots/data'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { SearchInput } from '@trellis/roots/forms'
import { Avatar } from '@trellis/roots/media'
import { PageHeader, TabBar } from '@trellis/roots/navigation'
import { ConfirmDialog } from '@trellis/roots/overlays'
import { BulkTagPicker, TagSelector } from '@trellis/roots/tags'
import { FEATURE } from '@trellis/shared'
import type { ClientStatus } from '@trellis/shared'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  MoreHorizontal,
  Plus,
  Tag,
  Trash2,
  Upload,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { ClientPreviewSheet } from '@/components/client-preview-sheet'
import { FilterSheet } from '@/components/shell'
import {
  useAssignClientTagsMutation,
  useBulkDelete,
  useBulkTagApply,
  useClientsQuery,
  useClientsSummaryQuery,
  useColumnVisibility,
  useDebouncedSearch,
  useDeleteClientMutation,
  useDisclosure,
  usePinnedColumn,
  useRemoveClientTagMutation,
  useTableSorting,
  useTagsQuery,
} from '@/hooks'
import { useIsFeatureEnabled } from '@/hooks/useFeatureFlags'
import { useSetBreadcrumbs } from '@/lib/breadcrumbs'
import { CLIENT_STATUS_LABELS, CLIENT_STATUS_STYLES } from '@/lib/client-status'
import { formatDate } from '@/lib/format'
import { buildTagsColumn, filterGatedColumns } from '@/lib/table-columns'
import type { ClientsListParams } from '@/requests/clients'
import { exportClients } from '@/requests/data-export'

export const ALL_CLIENT_STATUSES = ['ACTIVE', 'INACTIVE', 'PROSPECT'] as const

interface ClientsQuerySearchParams {
  status?: (typeof ALL_CLIENT_STATUSES)[number]
}

/**
 * Turns the clients route's validated search into the same options shape passed to
 * {@link useClientsQuery} here and to `clientsInfiniteQueryOptions` in the route's loader
 * — keeping both on the same query key so the loader's prefetch is actually hit. `search`
 * and `tags` are intentionally excluded: they're local component state, not URL-synced.
 */
export function buildClientsQueryOptions(
  search: ClientsQuerySearchParams,
): ClientsListParams {
  return {
    limit: 20,
    status: search.status ?? undefined,
  }
}

const clientsRouteApi = getRouteApi('/_authenticated/_dashboard/clients/')

type ClientStatusKey = ClientStatus | 'all'

const COLUMN_SPECS = [
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'company', label: 'Company' },
  { id: 'phone', label: 'Phone' },
  { id: 'status', label: 'Status' },
  { id: 'tags', label: 'Tags' },
  { id: 'createdAt', label: 'Created' },
]

const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  createdAt: false,
}

const STATUS_TABS: { label: string; value: ClientStatus | undefined }[] = [
  { label: 'All', value: undefined },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Prospective client', value: 'PROSPECT' },
  { label: 'Inactive', value: 'INACTIVE' },
]

function ClientStatusBadge({ status }: { status: ClientStatus }) {
  const { pill, dot } = CLIENT_STATUS_STYLES[status]
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${pill}`}
    >
      <span className={`size-1.5 shrink-0 rounded-full ${dot}`} />
      {CLIENT_STATUS_LABELS[status]}
    </span>
  )
}

function buildClientColumns(
  onDeleteClick: (client: ClientResponseDto) => void,
  onPreviewClick: (clientId: string) => void,
  isTagsEnabled: boolean,
): ColumnDef<ClientResponseDto>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      enableSorting: true,
      meta: { truncate: true },
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-2">
          <Avatar name={row.original.name} size="sm" />
          <span className="truncate">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      enableSorting: true,
      meta: { truncate: true },
    },
    {
      accessorKey: 'company',
      header: 'Company',
      enableSorting: true,
      meta: { truncate: true },
    },
    { accessorKey: 'phone', header: 'Phone', enableSorting: false },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <ClientStatusBadge status={row.original.status} />,
    },
    ...(isTagsEnabled ? [buildTagsColumn<ClientResponseDto>()] : []),
    {
      accessorKey: 'createdAt',
      header: 'Created',
      enableSorting: true,
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <RowActions
          linkComponent={Link}
          onViewClick={() => onPreviewClick(row.original.id)}
          triggerLabel={`Open actions menu for ${row.original.name}`}
          actions={[
            {
              label: 'Edit',
              icon: <Edit className="mr-2 h-4 w-4" />,
              href: `/clients/${row.original.id}/edit`,
            },
            {
              label: 'Delete',
              icon: <Trash2 className="mr-2 h-4 w-4" />,
              onClick: () => onDeleteClick(row.original),
              destructive: true,
              separator: true,
            },
          ]}
        />
      ),
    },
  ]
}

export function ClientsPage() {
  useSetBreadcrumbs([{ label: 'Clients' }])
  const navigate = useNavigate()
  const { inputValue, debouncedValue, setInputValue } = useDebouncedSearch()
  const clientDelete = useDisclosure<ClientResponseDto>()
  const clientPreview = useDisclosure<string>()
  const { status: statusFilter } = clientsRouteApi.useSearch()
  const navigateSearch = clientsRouteApi.useNavigate()
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { columnVisibility, onColumnVisibilityChange } = useColumnVisibility(
    'clients-columns',
    DEFAULT_COLUMN_VISIBILITY,
  )
  const { sorting, onSortingChange } = useTableSorting('clients-sorting')
  const { pinnedColumnId, onPinnedColumnChange } = usePinnedColumn(
    'clients-pinned-column',
  )

  const { isEnabled: isTagsEnabled } = useIsFeatureEnabled(FEATURE.TAGS)
  const allTags = useTagsQuery()

  const {
    isFetching,
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  } = useClientsQuery({
    ...buildClientsQueryOptions({ status: statusFilter ?? undefined }),
    search: debouncedValue,
    tags: tagFilter.length > 0 ? tagFilter : undefined,
  })
  const { data: summaryResult, isLoading: summaryLoading } = useClientsSummaryQuery()
  const deleteMutation = useDeleteClientMutation()
  const assignClientTagsMutation = useAssignClientTagsMutation()
  const removeClientTagMutation = useRemoveClientTagMutation()

  const rows = currentPageData?.data ?? []
  const selectedClients = rows.filter((row) => rowSelection[row.id])

  function clearSelection() {
    setRowSelection({})
  }

  useEffect(() => {
    setPageIndex(0)
  }, [statusFilter, debouncedValue, tagFilter, setPageIndex])

  function handleStatusChange(status: ClientStatus | undefined) {
    void navigateSearch({ search: (prev) => ({ ...prev, status }) })
  }

  const allClients = summaryResult?.data ?? []
  const clientColumns = buildClientColumns(
    clientDelete.open,
    clientPreview.open,
    isTagsEnabled,
  )
  const columnSpecs = filterGatedColumns(COLUMN_SPECS, isTagsEnabled)

  const tabCounts: Record<ClientStatusKey, number> = {
    all: allClients.length,
    ACTIVE: allClients.filter((client) => client.status === 'ACTIVE').length,
    PROSPECT: allClients.filter((client) => client.status === 'PROSPECT').length,
    INACTIVE: allClients.filter((client) => client.status === 'INACTIVE').length,
  }

  const {
    open: bulkDeleteOpen,
    setOpen: setBulkDeleteOpen,
    handleBulkDelete,
  } = useBulkDelete({
    selected: selectedClients,
    getId: (client) => client.id,
    deleteFn: (id) => deleteMutation.mutateAsync(id),
    label: 'client',
    onSuccess: clearSelection,
  })

  const {
    open: bulkTagPickerOpen,
    setOpen: setBulkTagPickerOpen,
    handleBulkTagApply,
    allSelectedTagIds,
    someSelectedTagIds,
  } = useBulkTagApply({
    selected: selectedClients,
    getId: (client) => client.id,
    getTags: (client) => client.tags ?? [],
    assignFn: (id, tagIds) =>
      assignClientTagsMutation.mutateAsync({ clientId: id, tagIds }),
    removeFn: (id, tagId) => removeClientTagMutation.mutateAsync({ clientId: id, tagId }),
    invalidateKey: ['clients'],
    onSuccess: clearSelection,
  })

  async function handleConfirmDelete() {
    const client = clientDelete.value
    if (!client) return
    clientDelete.close()
    try {
      await deleteMutation.mutateAsync(client.id)
      toast.success('Client deleted')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Failed to delete client'
      toast.error('Cannot delete client', { description: message })
    }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Clients"
        subtitle="Manage your client relationships."
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="More options"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => exportClients()}>
                  <Download className="mr-2 size-4" />
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Import</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/clients/import">
                    <Upload className="mr-2 size-4" />
                    Import CSV
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button asChild>
              <Link to="/clients/new">
                <Plus className="size-4" />
                New client
              </Link>
            </Button>
          </>
        }
      />

      {/* Status tabs */}
      <TabBar
        tabs={STATUS_TABS.map(({ label, value }) => ({
          key: value ?? 'all',
          label,
          count: tabCounts[value ?? 'all'],
        }))}
        activeKey={statusFilter ?? 'all'}
        onChange={(key) =>
          handleStatusChange(key === 'all' ? undefined : (key as ClientStatus))
        }
        isLoading={summaryLoading}
      />

      {/* Toolbar / Bulk action bar */}
      {selectedClients.length > 0 ? (
        <BulkActionBar
          selectedCount={selectedClients.length}
          actions={[
            ...(isTagsEnabled
              ? [
                  {
                    label: 'Assign tags',
                    icon: <Tag className="size-4" />,
                    onClick: () => setBulkTagPickerOpen(true),
                  },
                ]
              : []),
            {
              label: 'Delete',
              icon: <Trash2 className="size-4" />,
              onClick: () => setBulkDeleteOpen(true),
              variant: 'destructive',
            },
          ]}
          onClearSelection={clearSelection}
          className="mt-3 mb-3"
        />
      ) : (
        <div className="mt-3 mb-3">
          <FilterSheet
            activeCount={tagFilter.length > 0 ? 1 : 0}
            onClear={() => setTagFilter([])}
            toolbar={
              <>
                {isTagsEnabled && (
                  <TagSelector
                    tags={allTags.data ?? []}
                    value={tagFilter}
                    onChange={setTagFilter}
                    variant="filter"
                  />
                )}
                <div className="flex-1" />
                <SearchInput
                  placeholder="Search clients…"
                  value={inputValue}
                  onChange={(changeEvent) => setInputValue(changeEvent.target.value)}
                  className="max-w-sm"
                />
                <ColumnVisibilityMenu
                  columns={columnSpecs}
                  columnVisibility={columnVisibility}
                  onColumnVisibilityChange={onColumnVisibilityChange}
                />
              </>
            }
            sheet={
              isTagsEnabled ? (
                <TagSelector
                  tags={allTags.data ?? []}
                  value={tagFilter}
                  onChange={setTagFilter}
                  variant="filter"
                />
              ) : null
            }
          />
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={clientColumns}
        data={rows}
        isLoading={isFetching && rows.length === 0}
        emptyMessage="No clients found."
        className="rounded-b-none"
        onRowClick={(client) =>
          void navigate({ to: '/clients/$id', params: { id: client.id } })
        }
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        sorting={sorting}
        onSortingChange={onSortingChange}
        enableColumnPinning
        pinnedColumnId={pinnedColumnId}
        onPinnedColumnChange={onPinnedColumnChange}
        enableSelection={true}
        getRowId={(row) => row.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
      />
      {/* Pagination footer */}
      <div className="bg-muted/30 text-muted-foreground mt-0 flex items-center justify-between rounded-b-lg border-x border-b px-4 py-3 text-[12.5px]">
        <span>
          Page {pageIndex + 1} of {totalPages}
          {hasNextPage ? '+' : ''}
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={pageIndex === 0}
            onClick={goToPrevPage}
            className="h-7 text-xs"
          >
            <ChevronLeft className="mr-1 size-3.5" />
            Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasNextPage || isFetching}
            onClick={goToNextPage}
            className="h-7 text-xs"
          >
            Next
            <ChevronRight className="ml-1 size-3.5" />
          </Button>
        </div>
      </div>

      <ClientPreviewSheet
        clientId={clientPreview.value}
        onOpenChange={clientPreview.onOpenChange}
      />

      <ConfirmDialog
        open={clientDelete.isOpen}
        onOpenChange={clientDelete.onOpenChange}
        title="Are you absolutely sure?"
        description={
          <>
            This will permanently delete <strong>{clientDelete.value?.name}</strong>. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedClients.length} client${selectedClients.length === 1 ? '' : 's'}?`}
        description={`This will permanently delete ${selectedClients.length} client${selectedClients.length === 1 ? '' : 's'}. This action cannot be undone.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={() => void handleBulkDelete()}
      />

      {isTagsEnabled && (
        <BulkTagPicker
          open={bulkTagPickerOpen}
          onOpenChange={setBulkTagPickerOpen}
          allTags={allTags.data ?? []}
          allSelectedTagIds={allSelectedTagIds}
          someSelectedTagIds={someSelectedTagIds}
          onApply={(toAdd, toRemove) => void handleBulkTagApply(toAdd, toRemove)}
          isApplying={
            assignClientTagsMutation.isPending || removeClientTagMutation.isPending
          }
        />
      )}
    </div>
  )
}
